import { Info, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../cn';

type NoticeProps = {
  title: string;
  children?: ReactNode;
  /**
   * `announcement` — solid navy with white text, for notices that genuinely warrant emphasis.
   * `plain` — white surface with a subtle border, for ordinary explanatory notices.
   */
  variant?: 'announcement' | 'plain';
  icon?: LucideIcon;
  role?: 'note' | 'status' | 'alert';
  className?: string;
};

/** The single notice treatment (ADR-005): solid or bordered, never tinted. */
export function Notice({
  title,
  children,
  variant = 'plain',
  icon: Icon = Info,
  role = 'note',
  className,
}: NoticeProps) {
  const announcement = variant === 'announcement';
  return (
    <div
      role={role}
      className={cn(
        'flex gap-3 rounded-lg p-4',
        announcement ? 'on-navy bg-navy-900 text-white' : 'border border-line-200 bg-white',
        className,
      )}
    >
      <Icon
        className={cn('mt-0.5 size-5 shrink-0', announcement ? 'text-white' : 'text-navy-900')}
        aria-hidden
      />
      <div className="flex flex-col gap-1">
        <p className={cn('font-semibold', announcement ? 'text-white' : 'text-navy-900')}>
          {title}
        </p>
        {children ? (
          <div className={cn('text-sm', announcement ? 'text-white' : 'text-ink-900')}>
            {children}
          </div>
        ) : null}
      </div>
    </div>
  );
}
